import _ from 'lodash'
import { ValueOf } from 'type-fest'

import { collectNestedObjectsKeys, hasAnyNestedObject } from 'utils/object'
import { cleanString } from 'utils/string'

import type { Option, Options, OptionStates } from './types'

export const isCheckboxNode = (value: Option | Options): value is Option =>
  value.label !== undefined && !hasAnyNestedObject(value)

export const isCollapsibleNode = (value: Option | Options): value is Options =>
  value.label !== undefined && hasAnyNestedObject(value)

export const defaultOptionStates: OptionStates = {
  isChecked: false,
  isCollapsed: true,
}

export const filterOptionsTree = (tree: Options, filter: Option['label']): null | Options => {
  const branchNodes: (keyof Options)[] = collectNestedObjectsKeys<Options>(tree)
  const isLeafNode: boolean = branchNodes.length === 0

  if (isLeafNode) {
    return cleanString(tree.label as unknown as Option['label']).includes(cleanString(filter))
      ? tree
      : null
  }

  const tempBranch: Options = {}
  fillBranch({ tempBranch, tree })
  const { hasMatch } = filterBranch({ branchNodes, filter, tempBranch, tree })

  return hasMatch ? tempBranch : null
}

const fillBranch = ({ tempBranch, tree }: { tempBranch: Options; tree: Options }): void => {
  Object.keys(tree).forEach((key: keyof Options): void => {
    if (!_.isObject(tree[key])) {
      tempBranch[key] = tree[key]
    }
  })
}

const filterBranch = ({
  branchNodes,
  filter,
  tempBranch,
  tree,
}: {
  branchNodes: (keyof Options)[]
  filter: Option['label']
  tempBranch: Options
  tree: Options
}): { hasMatch: boolean } => {
  let hasMatch = false

  branchNodes.forEach((key: keyof Options): void => {
    const filteredChild: null | Options = filterOptionsTree(tree[key] as unknown as Options, filter)
    if (filteredChild !== null) {
      tempBranch[key] = filteredChild as unknown as ValueOf<Options>
      hasMatch = true
    }
  })

  return { hasMatch }
}
